// ─────────────────────────────────────────────────────────────────────────────
// icsGenerator.js
//
// Generuje .ics súbory (iCalendar RFC 5545) pre tréningové udalosti.
// Súbor sa dá otvoriť v Google Calendar, Apple Calendar, Outlook i na mobile.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Naformátuje dátum + čas do formátu iCalendar (YYYYMMDDTHHMMSS).
 *
 * @param {string} date   - "YYYY-MM-DD"
 * @param {string} time   - "HH:MM" alebo "HH:MM:SS"
 * @returns {string}
 */
function toIcsDateTime(date, time) {
  const [y, m, d] = date.split("-");
  const [hh, mm, ss = "00"] = time.split(":");
  return `${y}${m}${d}T${hh}${mm}${ss}`;
}

/**
 * Generuje uid pre udalosť – jedinečný identifikátor.
 */
function generateUid(date, title) {
  return `${date}-${title.replace(/\s+/g, "-").toLowerCase()}-capoeira@capoeira.sk`;
}

/**
 * Vytvorí obsah .ics súboru pre jednu udalosť.
 * Voliteľne nastaví týždenné opakovanie (RRULE:FREQ=WEEKLY).
 *
 * @param {{
 *   date: string,
 *   title: string,
 *   description?: string,
 *   timeFrom: string,
 *   timeTo: string,
 *   location?: string,
 *   recurring?: boolean,
 * }} event
 * @returns {string} obsah .ics súboru
 */
export function generateIcs({ date, title, description = "", timeFrom, timeTo, location = "Bratislava", recurring = true }) {
  const dtStart = toIcsDateTime(date, timeFrom);
  const dtEnd = toIcsDateTime(date, timeTo);
  const uid = generateUid(date, title);
  const now = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15);

  // Escape špeciálnych znakov podľa RFC 5545
  const esc = (str) => str.replace(/[\\;,]/g, (c) => `\\${c}`).replace(/\n/g, "\\n");

  const rrule = recurring ? "\r\nRRULE:FREQ=WEEKLY" : "";

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Capoeira Bratislava//SK",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}Z`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${esc(title)}`,
    description ? `DESCRIPTION:${esc(description)}` : null,
    location ? `LOCATION:${esc(location)}` : null,
    `STATUS:CONFIRMED`,
    rrule,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter((line) => line !== null && line !== "")
    .join("\r\n");
}

/**
 * Stiahne .ics súbor do zariadenia používateľa.
 *
 * @param {Parameters<typeof generateIcs>[0]} event
 */
export function downloadIcs(event) {
  const content = generateIcs(event);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const filename = `${event.date}-${event.title.replace(/\s+/g, "-").toLowerCase()}.ics`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

/**
 * Otvorí Google Calendar s predvyplnenou udalosťou v novom tabe.
 *
 * @param {Parameters<typeof generateIcs>[0]} event
 */
export function openGoogleCalendar({ date, title, description = "", timeFrom, timeTo, location = "Bratislava", recurring = true }) {
  const dtStart = toIcsDateTime(date, timeFrom);
  const dtEnd = toIcsDateTime(date, timeTo);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${dtStart}/${dtEnd}`,
    location,
  });

  if (description) {
    params.set("details", description);
  }

  if (recurring) {
    params.set("recur", "RRULE:FREQ=WEEKLY");
  }

  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank", "noopener");
}
